<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { Color } from '@previo/codebooks'
import { usePrevioCore } from '@previo/core'
import { $t } from '../../composables/i18n'
import { PCardRound, PCardSize } from '../card/types'
import { PSpaceSize } from '../space/types'
import PBtnTable from '../btn-table/PBtnTable.vue'
import PCard from '../card/PCard.vue'
import PSpace from '../space/PSpace.vue'
import PTextStandard from '../typography/body/PTextStandard.vue'
import LabelInputField from '../fields/components/LabelInputField.vue'
import { usePlatform } from '../../composables/helpers'
import { PTextEditorButtons } from './types'

type Props = {
  color?: null | Color.WHITE | Color.NOTE_DARK
  placeholder?: null | string
  label?: null | string
  required?: boolean
  autoHideFormatBar?: boolean
  formatButtons?: PTextEditorButtons[]
  cy: string
  activateEdit?: boolean
  height?: null | string
}

const model = defineModel<null | string>({ default: null })
const isEditing = defineModel<boolean>('isEditing', { default: false })

const props = withDefaults(defineProps<Props>(), {
  color: Color.WHITE,
  required: false,
  autoHideFormatBar: false,
  activateEdit: false,
  height: null,
  formatButtons: () => Object.values(PTextEditorButtons),
})

const heightStyle = computed<undefined | string>(() =>
  props.height === null
    ? undefined
    : `min-height: ${props.height}; max-height: ${props.height}; overflow-y: auto`,
)

const HTMLRawComponent = ref<InstanceType<typeof HTMLElement>>()
const componentRootElement = ref<InstanceType<typeof HTMLElement>>()
const rawHTML = ref<string>('')

const { safeHtml } = usePrevioCore()
const { isAppleHandheldDevice } = usePlatform()
const isFormatBarAboveOnMobile = isAppleHandheldDevice()

const sanitizeUrl = (url: string): string => {
  const protocol = url.trim().toLowerCase()
  if (
    protocol.startsWith('javascript:') ||
    protocol.startsWith('data:') ||
    protocol.startsWith('vbscript:')
  ) {
    return 'about:blank'
  }
  return url
}

onMounted(() => {
  rawHTML.value = model.value ? safeHtml(model.value) : ''
  document.execCommand('defaultParagraphSeparator', false, 'p')
  if (props.activateEdit) {
    void startEdit()
  }
})

onClickOutside(componentRootElement, () => (isEditing.value = false))

const showPlaceholder = computed<boolean>(() => {
  return !model.value && !isEditing.value
})

const isFormatBarShow = computed<boolean>(() => {
  return isEditing.value || !props.autoHideFormatBar
})

const startEdit = async (): Promise<void> => {
  isEditing.value = true
  await nextTick()
  HTMLRawComponent.value?.focus()
}

const startEditWithCursorAtEnd = async (): Promise<void> => {
  isEditing.value = true
  await nextTick()
  const element = HTMLRawComponent.value

  if (element) {
    const range = document.createRange()
    const selection = window.getSelection()

    range.selectNodeContents(element)
    range.collapse(false)
    selection?.removeAllRanges()
    selection?.addRange(range)
  }
}

const change = (event: Event): void => {
  const target = event.target

  if (!(target instanceof HTMLElement)) {
    return
  }

  // Need remove last "<br>" tag that browser automatically insert
  model.value = target.innerHTML.replace(/<br>\s*$/, '')
}

const applyBold = (): void => {
  document.execCommand('bold')
}

const applyItalic = (): void => {
  document.execCommand('italic')
}

const applyUnderline = (): void => {
  document.execCommand('underline')
}

const applyLink = (): void => {
  const linkURL = prompt($t('Write a URL:'), 'https://')
  const selection = document.getSelection()

  document.execCommand(
    'insertHTML',
    false,
    '<a href="' + sanitizeUrl(linkURL ?? '') + '" target="_blank">' + selection + '</a>',
  )
}

const applyUl = (): void => {
  document.execCommand('insertUnorderedList')
}

const applyOl = (): void => {
  document.execCommand('insertOrderedList')
}

const undo = (): void => {
  document.execCommand('undo')
}

const redo = (): void => {
  document.execCommand('redo')
}

const clear = () => {
  model.value = null
  rawHTML.value = ''
}

defineExpose({ clear, startEdit, startEditWithCursorAtEnd })
</script>

<template>
  <label-input-field :label="label" :is-required="required" />

  <div ref="componentRootElement" class="ds-text-editor">
    <div
      class="ds-text-editor-html"
      :class="{
        'p-cursor-pointer': !isEditing,
      }"
      :style="heightStyle"
    >
      <p-space :mobile="PSpaceSize.ZERO" :laptop="PSpaceSize.SM" vertical />
      <div v-if="showPlaceholder" @click="startEdit">
        <p-text-standard :text="placeholder" :color="Color.GREY_400" />
      </div>

      <div class="p-display-flex">
        <p-space :mobile="PSpaceSize.SM" />
        <!-- eslint-disable vue/no-v-html -->
        <div
          ref="HTMLRawComponent"
          contenteditable="true"
          class="p-full-width"
          :style="showPlaceholder ? 'height: 0' : undefined"
          @input="change"
          @click="startEdit"
          v-html="rawHTML"
        />
        <!--eslint-enable vue/no-v-html -->
        <p-space :mobile="PSpaceSize.SM" />
      </div>

      <p-space :mobile="PSpaceSize.MD" :laptop="PSpaceSize.ZERO" vertical />
    </div>

    <p-card
      v-if="isFormatBarShow"
      class="ds-text-editor-format-bar p-flex p-shadow-200"
      :class="{ 'ds-text-editor-format-bar--above-mobile': isFormatBarAboveOnMobile }"
      :pa-mobile="PCardSize.SM"
      :round="PCardRound.LG"
      :color="color"
      :data-cy="cy"
    >
      <p-btn-table
        v-if="formatButtons.includes(PTextEditorButtons.BOLD)"
        icon="bold"
        cy="bold"
        :color="Color.TRANSPARENT"
        @click="applyBold"
      />
      <p-btn-table
        v-if="formatButtons.includes(PTextEditorButtons.ITALIC)"
        icon="italic"
        cy="italic"
        :color="Color.TRANSPARENT"
        @click="applyItalic"
      />
      <p-btn-table
        v-if="formatButtons.includes(PTextEditorButtons.UNDERLINE)"
        icon="underline"
        cy="underline"
        :color="Color.TRANSPARENT"
        @click="applyUnderline"
      />
      <p-space :mobile="PSpaceSize.SM" />

      <p-btn-table
        v-if="formatButtons.includes(PTextEditorButtons.LIST_OL)"
        icon="list-ol"
        cy="list-ol"
        :color="Color.TRANSPARENT"
        @click="applyOl"
      />
      <p-btn-table
        v-if="formatButtons.includes(PTextEditorButtons.LIST_UL)"
        icon="list-ul"
        cy="list-ul"
        :color="Color.TRANSPARENT"
        @click="applyUl"
      />
      <p-space :mobile="PSpaceSize.SM" />

      <p-btn-table
        v-if="formatButtons.includes(PTextEditorButtons.LINK)"
        icon="link"
        cy="link"
        :color="Color.TRANSPARENT"
        @click="applyLink"
      />
      <p-space :mobile="PSpaceSize.GROW" />

      <p-btn-table icon="arrow-rotate-left" cy="back" :color="Color.TRANSPARENT" @click="undo" />
      <p-btn-table
        icon="arrow-rotate-right"
        cy="forward"
        :color="Color.TRANSPARENT"
        @click="redo"
      />
    </p-card>
  </div>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/mixins';

.ds-text-editor {
  display: grid;
}

.ds-text-editor-html {
  word-break: break-word;
  @include laptopAndLarge {
    order: 2;
  }
}

.ds-text-editor-format-bar {
  @include laptopAndLarge {
    order: 1;
  }

  &--above-mobile {
    @include mobile {
      order: -1;
    }
  }
}

// Remove outline of input (editable content in HTML)
[contenteditable]:focus {
  outline: 0 solid transparent;
}
</style>
